import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

// mui components
import { Box, Stack } from "@mui/material";

// custom components
import CustomDivider from "../custom-divider";
import Heading6 from "../typography/heading-06";
import Body2 from "../typography/body-02";
import DirectPathBadge from "../direct-path-badge";

// api slices
import { useGetLoanSummaryQuery } from "../../features/loanSummary/loanSummaryApiSlice";
import { useFieldMutation } from "../../features/field-values/fieldValueApiSlice";

// reducer slices
import { selectUserDetails, selectSelectedOrganization } from "../../features/auth/authSlice";

// helper utils
import {
	DIRECT_PATH_CONSTANTS,
	isDirectPathYes,
} from "../../utils/direct-path.constants";
import { findFieldValue } from "../../utils/filter-value";

// custom styles
import styles from "./index.module.scss";

const LoanInfoCard = ({ loanId }) => {
	const params = useParams();
	const userDetails = useSelector(selectUserDetails);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const loanGuid = params?.id || loanId;
	const { data: loanData } = useGetLoanSummaryQuery({
		loanId: loanGuid,
		selectedOrganization,
		userDetails,
	});
	const [field] = useFieldMutation();

	const [borrowerInfo, setBorrowerInfo] = useState({});
	const [coborrowerInfo, setCoborrowerInfo] = useState({});
	const [propertyInfo, setPropertyInfo] = useState({});
	const [loanNumber, setLoanNumber] = useState("");
	const [showDirectPathBadge, setShowDirectPathBadge] = useState(false);

	useEffect(() => {
		console.log("30 loan data:", loanData);

		setBorrowerInfo(loanData?.data?.applications[0]?.borrower);
		setCoborrowerInfo(loanData?.data?.applications[0]?.coborrower);
		setPropertyInfo(loanData?.data?.property);
		setLoanNumber(loanData?.data?.loanNumber);
	}, [loanData]);

	useEffect(() => {
		const fetchDirectPathField = async () => {
			if (!loanGuid || !userDetails || selectedOrganization === undefined) {
				setShowDirectPathBadge(false);
				return;
			}

			try {
				const getValues = await field({
					loan_guid: loanGuid,
					fields: DIRECT_PATH_CONSTANTS.ENCOMPASS_FIELD_ID,
					selectedOrganization,
					userDetails,
				}).unwrap();
				setShowDirectPathBadge(
					isDirectPathYes(
						findFieldValue(
							getValues?.response,
							DIRECT_PATH_CONSTANTS.ENCOMPASS_FIELD_ID
						)
					)
				);
			} catch (err) {
				setShowDirectPathBadge(false);
			}
		};

		fetchDirectPathField();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid, selectedOrganization, userDetails]);

	return (
		<Stack direction="row" className={styles.cardContainer}>
			{/* loan number */}
			<Stack direction="column">
				<Heading6 text="Loan Number" fontType="semibold" />
				<Body2 text={loanNumber || ""} fontType="semibold" />
			</Stack>

			<Box className={styles.dividerContainer}>
				<CustomDivider extraClass={styles.divider} />
			</Box>

			{/* borrower */}
			<Stack direction="column">
				<Heading6 text="Borrower " fontType="semibold" />
				<Body2
					text={`${borrowerInfo?.firstName || ""} ${borrowerInfo?.middleName || ""} ${
						borrowerInfo?.lastName || ""
					}`}
					fontType="semibold"
				/>
			</Stack>

			<Box className={styles.dividerContainer}>
				<CustomDivider extraClass={styles.divider} />
			</Box>
			{/* co-borrower */}
			<Stack direction="column">
				<Heading6 text="Co Borrower " fontType="semibold" />
				<Body2
					text={`${coborrowerInfo?.firstName || ""} ${
						coborrowerInfo?.middleName || ""
					} ${coborrowerInfo?.lastName || ""}`}
					fontType="semibold"
				/>
			</Stack>

			<Box className={styles.dividerContainer}>
				<CustomDivider extraClass={styles.divider} />
			</Box>

			{/* property address */}
			<Stack direction="column">
				<Heading6 text="Property Address " fontType="semibold" />
				<Body2
					text={`${propertyInfo?.addressLineText || ""} ${
						propertyInfo?.city || ""
					} ${propertyInfo?.state || ""} ${propertyInfo?.postalCode || ""}`}
					fontType="semibold"
				/>
			</Stack>

			{showDirectPathBadge && (
				<Stack direction="column" className={styles.badgeContainer}>
					<Heading6
						text={DIRECT_PATH_CONSTANTS.PROCESSING_LABEL}
						fontType="semibold"
					/>
					<DirectPathBadge />
				</Stack>
			)}
		</Stack>
	);
};

export default LoanInfoCard;
